import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Play, 
  Pause, 
  Music,
  Trash2,
  Upload,
  ChevronDown,
  GripVertical
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MobilePlaylist = React.memo(function MobilePlaylist({
  isOpen,
  onClose,
  playlist = [],
  currentIndex = 0,
  isPlaying = false,
  onFileUpload = () => {},
  playSongAtIndex = () => {},
  removeSong = () => {},
  reorderSongs = () => {},
  handlePlayPause = () => {},
}) {
  // Use a single ref for all mutable drag state so document-level listeners
  // always read live values — no stale closure issues.
  const dragStateRef = React.useRef({
    draggedIndex: null,
    dragOverIndex: null,
    touchStartY: null,
    isDragging: false,
  });
  // Separate visual state drives re-renders for UI feedback only.
  const [dragVisual, setDragVisual] = React.useState({
    draggedIndex: null,
    dragOverIndex: null,
    isDragging: false,
  });
  const itemRefs = React.useRef([]);
  // Keep reorderSongs in a ref so handlers don't need it as a dependency.
  const reorderSongsRef = React.useRef(reorderSongs);
  React.useEffect(() => { reorderSongsRef.current = reorderSongs; }, [reorderSongs]);
  
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onFileUpload(files);
    }
  };

  // Drag-to-reorder via Pointer Events + setPointerCapture.
  // setPointerCapture routes ALL subsequent pointermove/pointerup to the grip element
  // even when the finger moves far outside it — no document listeners needed, works
  // reliably on both iOS Safari and Android Chrome.
  const handlePointerDown = (e, index) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = {
      draggedIndex: index,
      dragOverIndex: null,
      startY: e.clientY,
      isDragging: false,
    };
    setDragVisual({ draggedIndex: index, dragOverIndex: null, isDragging: false });
    if (navigator.vibrate) navigator.vibrate(50);
  };

  const handlePointerMove = (e) => {
    const ds = dragStateRef.current;
    if (ds.draggedIndex === null) return;

    const currentY = e.clientY;
    const deltaY = Math.abs(currentY - ds.startY);

    if (!ds.isDragging) {
      if (deltaY > 8) {
        dragStateRef.current.isDragging = true;
        setDragVisual(prev => ({ ...prev, isDragging: true }));
      } else {
        return;
      }
    }

    // Hit-test which row the finger is over
    let foundIndex = null;
    itemRefs.current.forEach((ref, idx) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        if (currentY >= rect.top && currentY <= rect.bottom) {
          foundIndex = idx;
        }
      }
    });

    if (foundIndex !== null && foundIndex !== ds.dragOverIndex) {
      dragStateRef.current.dragOverIndex = foundIndex;
      setDragVisual(prev => ({ ...prev, dragOverIndex: foundIndex }));
      if (navigator.vibrate) navigator.vibrate(10);
    }
  };

  const handlePointerUp = () => {
    const ds = dragStateRef.current;
    if (
      ds.isDragging &&
      ds.draggedIndex !== null &&
      ds.dragOverIndex !== null &&
      ds.draggedIndex !== ds.dragOverIndex
    ) {
      reorderSongsRef.current(ds.draggedIndex, ds.dragOverIndex);
      if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
    }
    dragStateRef.current = { draggedIndex: null, dragOverIndex: null, startY: null, isDragging: false };
    setDragVisual({ draggedIndex: null, dragOverIndex: null, isDragging: false });
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex-none flex items-center justify-between px-4 py-4 border-b border-white/10 bg-black/30 backdrop-blur-sm" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <Button
          variant="ghost"
          size="icon"
          className="text-white h-10 w-10"
          onClick={onClose}
        >
          <ChevronDown className="h-6 w-6" />
        </Button>
        
        <div className="text-base font-bold text-white">
          {playlist.length} {playlist.length === 1 ? 'Song' : 'Songs'}
        </div>
        
        <label htmlFor="mobile-file-upload" className="cursor-pointer">
          <Button
            variant="ghost"
            size="icon"
            className="text-white h-10 w-10 hover:bg-white/10"
            asChild
          >
            <div>
              <Upload className="h-5 w-5" />
            </div>
          </Button>
          <input
            id="mobile-file-upload"
            type="file"
            multiple
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/flac,audio/aac,audio/m4a,.mp3,.wav,.ogg,.flac,.aac,.m4a"
            className="hidden"
            onChange={handleFileInput}
          />
        </label>
      </div>

      {/* Playlist content */}
      <div className={cn("flex-1 overflow-y-auto", dragVisual.isDragging && "overflow-hidden touch-none")}>
        {playlist.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-600/20 flex items-center justify-center mb-6 backdrop-blur-sm border border-white/10">
              <Music className="h-12 w-12 text-white/30" />
            </div>
            <p className="text-white/70 text-lg font-medium mb-2">Your playlist is empty</p>
            <p className="text-white/50 text-sm mb-6">Add songs to start listening</p>
            <label htmlFor="mobile-file-upload-empty" className="cursor-pointer">
              <Button variant="default" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg" asChild>
                <div className="flex items-center gap-2 px-6 py-3">
                  <Upload className="h-4 w-4" />
                  <span className="font-medium">Add Songs</span>
                </div>
              </Button>
              <input
                id="mobile-file-upload-empty"
                type="file"
                multiple
                accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/flac,audio/aac,audio/m4a,.mp3,.wav,.ogg,.flac,.aac,.m4a"
                className="hidden"
                onChange={handleFileInput}
              />
            </label>
          </div>
        ) : (
          <div className="p-3 pb-safe">
            {playlist.map((song, index) => (
              <motion.div
                key={song.id || index}
                ref={(el) => (itemRefs.current[index] = el)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.02, 0.3) }}
                className={cn(
                  "flex items-center gap-3 p-4 rounded-xl mb-2 transition-all",
                  dragVisual.isDragging && dragVisual.draggedIndex === index && "opacity-30 scale-95 shadow-2xl",
                  dragVisual.isDragging && dragVisual.dragOverIndex === index && dragVisual.draggedIndex !== null && dragVisual.draggedIndex !== index && "border-t-4 border-t-blue-400",
                  index === currentIndex
                    ? "bg-gradient-to-r from-blue-600/30 to-indigo-600/30 border border-blue-500/50 shadow-lg"
                    : "bg-white/5 active:bg-white/10 border border-transparent"
                )}
              >
                {/* Drag handle — pointer capture routes all move/up events here */}
                <div
                  className="text-white/40 select-none p-2 -m-2 cursor-grab active:cursor-grabbing"
                  style={{ touchAction: 'none', userSelect: 'none' }}
                  onPointerDown={(e) => handlePointerDown(e, index)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                >
                  <GripVertical className="h-5 w-5 pointer-events-none" />
                </div>
                {/* Play indicator or index */}
                <div 
                  className="flex items-center justify-center w-10 h-10 shrink-0"
                  onClick={() => playSongAtIndex(index)}
                >
                  {index === currentIndex ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-white hover:bg-white/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayPause();
                      }}
                    >
                      {isPlaying ? (
                        <Pause className="h-5 w-5" fill="currentColor" />
                      ) : (
                        <Play className="h-5 w-5" fill="currentColor" />
                      )}
                    </Button>
                  ) : (
                    <span className="text-white/50 text-sm font-medium">
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Song info */}
                <div 
                  className="flex-1 min-w-0"
                  onClick={() => playSongAtIndex(index)}
                >
                  <div className={cn(
                    "font-semibold truncate mb-0.5",
                    index === currentIndex ? "text-blue-300" : "text-white"
                  )}>
                    {song.name}
                  </div>
                  <div className="text-xs text-white/50 flex items-center gap-2">
                    <span>{formatDuration(song.duration)}</span>
                    {index === currentIndex && isPlaying && (
                      <span className="flex items-center gap-1">
                        <span className="w-1 h-1 bg-blue-400 rounded-full animate-pulse"></span>
                        <span className="text-blue-400">Playing</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Delete button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-red-400/80 hover:text-red-300 hover:bg-red-500/20 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSong(index);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
});

export default MobilePlaylist;
